import { useRef, useState } from "react";
import { ChevronDownIcon, LockIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { useScope } from "@/hooks/useScope";
import { iconsByTypename } from "@/objects/@icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CHIP_WORDS_CLASS } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.tools";
import { Button } from "@/components/ui/button";
import { ButtonGroupText } from "@/components/ui/button-group";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";

/** One place a new chat can be made: a business, or ``null`` - the person's own. */
type ThreadScopeRow = {
  identifier: string | null;
  name: string;
  description: string;
};

/**
 * Whose a chat is, at the start of its composer. A business's chat is seen by
 * everyone there, and reads and changes that business's records alone; a
 * person's own is private, and works with every business they have selected.
 * Before its first message, it is where the chat will be made, picked from
 * their businesses or kept their own; made, a chat's business is its for good.
 */
export function ThreadScopeChip({
  entity,
  pickedIdentifier,
  onPick,
}: {
  /** The chat's business, once it is made. */
  entity: {
    identifier?: string | null;
    name?: string | null;
    isUserEntity?: boolean | null;
  } | null;
  /** Not made yet: the business picked for it, or ``null`` - the person's own. */
  pickedIdentifier: string | null;
  /** Not made yet: picks where it is made. Made, there is nothing to pick. */
  onPick: ((identifier: string | null) => void) | null;
}) {
  const { nonUserEntities, activeEntities } = useScope();
  const [isOpen, setIsOpen] = useState(false);
  // Where the keyboard was as it opened, given back as it shuts.
  const focusedRef = useRef<HTMLElement | null>(null);

  const business = entity
    ? entity.isUserEntity
      ? null
      : entity
    : (nonUserEntities.find(
        (candidate) => candidate?.identifier === pickedIdentifier,
      ) ?? null);
  const name = business?.name ?? "this business";
  // What a private chat works with: the businesses selected as it is sent.
  const reached = activeEntities
    .filter((candidate) => candidate && !candidate.isUserEntity)
    .map((candidate) => candidate?.name)
    .filter((candidate): candidate is string => Boolean(candidate));
  const reach =
    reached.length > 1
      ? `${reached.slice(0, -1).join(", ")} and ${reached[reached.length - 1]}`
      : (reached[0] ?? "your businesses");
  const says = business
    ? `Everyone at ${name} can see this chat. It reads and changes ${name}'s records only.`
    : `Only you can see this chat. It works with ${reach}.`;

  const toggle = () => {
    if (!isOpen) {
      focusedRef.current = document.activeElement as HTMLElement | null;
    }
    setIsOpen(!isOpen);
  };
  const words = (
    <>
      <Icon
        ReactIcon={business ? iconsByTypename.EntityObject : LockIcon}
        className="text-muted-foreground"
      />
      {business ? name : "Private"}
    </>
  );
  // A button, as a filter's is - and, made, words where it was, as a locked
  // filter's are (``h-8``, a button's height beside them): there is nothing
  // to press.
  const chip = onPick ? (
    <Button
      variant="outline"
      size="sm"
      data-testid="thread-scope"
      data-private={business ? undefined : ""}
      // Open, the keyboard is in its list, off the composer: the composer is
      // drawn as still having it.
      data-keeps-focus-ring={isOpen || undefined}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      onClick={toggle}
    >
      {words}
      <Icon ReactIcon={ChevronDownIcon} />
    </Button>
  ) : (
    <ButtonGroupText
      data-testid="thread-scope"
      data-private={business ? undefined : ""}
      className={cn(CHIP_WORDS_CLASS, "h-8 text-muted-foreground")}
    >
      {words}
    </ButtonGroupText>
  );

  if (!onPick) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex">{chip}</div>
        </TooltipTrigger>
        <TooltipContent side="top">{says}</TooltipContent>
      </Tooltip>
    );
  }

  const rows: ThreadScopeRow[] = [
    {
      identifier: null,
      name: "Private",
      description: `Only you can see it. It works with ${reach}.`,
    },
    ...nonUserEntities.flatMap((candidate) =>
      candidate?.identifier
        ? [
            {
              identifier: candidate.identifier,
              name: candidate.name ?? "Business",
              description: `Everyone at ${candidate.name ?? "it"} can see it.`,
            },
          ]
        : [],
    ),
  ];

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      // Its chip opens it.
      isClickable={false}
      placement="top-start"
      floatingStyle={{ width: 300 }}
      focusRestoreRef={focusedRef}
      anchor={
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex">{chip}</div>
          </TooltipTrigger>
          <TooltipContent side="top">{isOpen ? "" : says}</TooltipContent>
        </Tooltip>
      }
    >
      <div data-testid="thread-scope-panel">
        <List<ThreadScopeRow>
          data={rows}
          getRowKey={(row) => row.identifier ?? "private"}
          onItemClick={(row) => {
            setIsOpen(false);
            onPick(row.identifier);
          }}
          autofocus
          fillHeight={false}
          size="sm"
        >
          <List.Items<ThreadScopeRow>>
            {(row: ThreadScopeRow) => (
              <List.Item
                value={row}
                data-testid={`thread-scope-${row.identifier ?? "private"}`}
              >
                <div className="pl-1 pr-1 pt-0.5 pb-0.5 min-w-0">
                  <Typography level="body-md">
                    {row.name}
                    {row.identifier === pickedIdentifier ? " ✓" : ""}
                  </Typography>
                  <Typography
                    level="body-xs"
                    className="text-muted-foreground/80 whitespace-normal"
                  >
                    {row.description}
                  </Typography>
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      </div>
    </Popper>
  );
}
